import React from 'react';
import { LucideIcon } from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { clsx } from 'clsx';

export interface IconGridItem {
  icon: LucideIcon;
  title: string;
  description: string;
  bullets?: string[];
}

interface IconGridProps {
  items: IconGridItem[];
  columns?: 2 | 3 | 4;
  className?: string;
}

export const IconGrid: React.FC<IconGridProps> = ({
  items,
  columns = 4,
  className,
}) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const gridCols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div ref={ref} className={clsx('grid gap-8', gridCols[columns], className)}>
      {items.map((item, i) => (
        <div
          key={item.title}
          className={clsx(
            'text-center sm:text-left transition-all duration-700 ease-out delay-75',
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          )}
          style={{ transitionDelay: inView ? `${i * 75}ms` : '0ms' }}
        >
          {/* ✅ FIX: icon always visible even if parent forces text-white */}
          <div
            className={clsx(
              'inline-flex sm:flex h-12 w-12 items-center justify-center rounded-xl',
              // Always readable in light + dark (won’t become “white on white”)
              'bg-black/25 dark:bg-white/10',
              'ring-1 ring-white/20',
              'text-white',
              'backdrop-blur',
              'hover:bg-black/35 dark:hover:bg-white/15 transition-colors'
            )}
          >
            <item.icon className="h-6 w-6 text-white" aria-hidden />
          </div>

          <h3 className="mt-4 text-lg font-semibold text-app-text dark:text-white">
            {item.title}
          </h3>

          <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400">
            {item.description}
          </p>

          {item.bullets && item.bullets.length > 0 && (
            <ul className="mt-3 space-y-1.5 text-sm text-app-text-muted dark:text-gray-400">
              {item.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary-500" />
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};
